"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.handleCaseInsensitiveUrl = exports.handleDeploymentProtection = exports.handleErrorRedirect = exports.handleLocaleRedirect = exports.COOKIES = void 0;
exports.createBuilderRedirectHandler = createBuilderRedirectHandler;
const server_1 = require("next/server");
// Percent-decode a query token the same way URLSearchParams does (so a source
// like "tfa_397=East%20FL" compares equal to the request's decoded "East FL"),
// then lowercase to match the rest of the redirect pipeline. "+" is treated as
// a space, per application/x-www-form-urlencoded. Falls back to the raw token
// (lowercased) if it contains an invalid escape sequence.
function decodeQueryToken(token) {
    try {
        return decodeURIComponent(token.replace(/\+/g, " ")).toLowerCase();
    }
    catch {
        return token.toLowerCase();
    }
}
// Parse a raw source query string ("a&b=c") into presence/equality
// constraints. Keys and values are decoded + lowercased to match the request
// side, which URLSearchParams already decodes and matchQuery lowercases.
function parseQueryConstraints(query) {
    return query
        .split("&")
        .filter(Boolean)
        .map((token) => {
        const eq = token.indexOf("=");
        if (eq === -1)
            return { key: decodeQueryToken(token), value: null };
        return { key: decodeQueryToken(token.slice(0, eq)), value: decodeQueryToken(token.slice(eq + 1)) };
    });
}
// "Exact param present": every constraint key must appear in the request; a
// constraint carrying a value must match that value (case-insensitive). Returns
// the set of consumed (lowercased) keys so the caller can forward the leftover
// params to the destination, or null when the request does not satisfy the rule.
function matchQuery(constraints, params) {
    if (constraints.length === 0)
        return new Set();
    const lowerParams = new Map();
    for (const [k, v] of params) {
        const lk = k.toLowerCase();
        if (!lowerParams.has(lk))
            lowerParams.set(lk, v.toLowerCase());
    }
    const consumed = new Set();
    for (const c of constraints) {
        if (!lowerParams.has(c.key))
            return null;
        if (c.value !== null && lowerParams.get(c.key) !== c.value)
            return null;
        consumed.add(c.key);
    }
    return consumed;
}
// Append request params the match did not consume to the destination, without
// overriding params the destination already sets. Fixes the historical
// "query string dropped on redirect" bug while letting query-matched params be
// consumed. baseHref must be absolute (callers resolve relative destinations).
function forwardLeftoverQuery(baseHref, params, consumed) {
    let hasLeftover = false;
    for (const key of params.keys()) {
        if (!consumed.has(key.toLowerCase())) {
            hasLeftover = true;
            break;
        }
    }
    if (!hasLeftover)
        return baseHref;
    const url = new URL(baseHref);
    for (const [key, value] of params) {
        if (consumed.has(key.toLowerCase()))
            continue;
        if (url.searchParams.has(key))
            continue; // destination's own query wins
        url.searchParams.append(key, value);
    }
    return url.href;
}
function createBuilderRedirectHandler(redirects) {
    // The same path can be redirected differently per domain (e.g. "/" on
    // winniepalmerhospital.com vs blog.winniepalmerhospital.com) AND per query
    // (e.g. "/home?a" vs "/home?b"), so each source maps to a LIST of candidates
    // filtered by fromDomain and query constraints at request time.
    const exactMap = new Map();
    const patternList = [];
    for (const entry of redirects) {
        const constraints = entry.query ? parseQueryConstraints(entry.query) : [];
        if (entry.source.includes("(.*)")) {
            // Make a terminal /(.*)  optional so the base path (no sub-path) also matches.
            // e.g. /institute/(.*) becomes /institute(?:/(.*))? which matches /institute
            // and /institute/foo, not just /institute/foo.
            const normalizedSource = entry.source.replace(/\/\(\.\*\)$/, "(?:/(.*))?");
            const regexSource = "^" + normalizedSource.replace(/\(\.\*\)/g, "(.*)") + "(?:/)?$";
            patternList.push({ regex: new RegExp(regexSource, "i"), entry, constraints });
        }
        else {
            const key = entry.source.toLowerCase();
            const list = exactMap.get(key);
            if (list) {
                list.push({ entry, constraints });
            }
            else {
                exactMap.set(key, [{ entry, constraints }]);
            }
        }
    }
    // Order candidates so the most specific wins: query-constrained entries
    // (more constraints first) before plain ones, then the most specific domain
    // (e.g. "blog.winniepalmerhospital.com" before "winniepalmerhospital.com").
    // Sort is stable, so with no query entries the order is identical to before.
    const compare = (a, b) => b.constraints.length - a.constraints.length ||
        (b.entry.fromDomain?.length ?? 0) - (a.entry.fromDomain?.length ?? 0);
    for (const list of exactMap.values())
        list.sort(compare);
    patternList.sort(compare);
    // `onHit` is optional so existing callers (oh-corp, oh-dch, oh-foundation)
    // are unaffected. It is called synchronously right before the response is
    // built; anything slow (a Redis write) belongs in event.waitUntil.
    return function handleBuilderRedirect(request, onHit) {
        const { pathname, hostname, searchParams } = new URL(request.url);
        const notify = (hit) => {
            if (!onHit)
                return;
            try {
                onHit(hit);
            }
            catch {
                // A tracking failure must never break the redirect.
            }
        };
        const lower = pathname.toLowerCase();
        const candidates = exactMap.get(lower);
        if (candidates) {
            for (const { entry, constraints } of candidates) {
                // Domain-scoped candidates take precedence over domain-less catch-alls
                // (ordering above); a non-matching domain is skipped, not a dead end.
                if (entry.fromDomain && !hostname.includes(entry.fromDomain))
                    continue;
                const consumed = matchQuery(constraints, searchParams);
                if (!consumed)
                    continue;
                const base = entry.destination.startsWith("http")
                    ? entry.destination
                    : new URL(entry.destination, request.url).href;
                const dest = forwardLeftoverQuery(base, searchParams, consumed);
                const status = entry.permanent ? 308 : 307;
                // Logged on every redirect that fires. Surfaces in Vercel Runtime Logs
                // so you can confirm which redirect matched a given request.
                console.log(`[redirect] HIT exact ${status} host=${hostname} path=${pathname} -> ${dest}`);
                notify({ entry, kind: "exact", hostname, pathname, destination: dest, status });
                return server_1.NextResponse.redirect(dest, { status });
            }
        }
        for (const { regex, entry, constraints } of patternList) {
            if (entry.fromDomain && !hostname.includes(entry.fromDomain))
                continue;
            const consumed = matchQuery(constraints, searchParams);
            if (!consumed)
                continue;
            const m = pathname.match(regex);
            if (!m)
                continue;
            const captured = m[1] ?? "";
            const destination = captured
                ? entry.destination.replace(/\$1/g, captured)
                : entry.destination.replace(/\/?\$1/g, "");
            const base = destination.startsWith("http")
                ? destination
                : new URL(destination, request.url).href;
            const dest = forwardLeftoverQuery(base, searchParams, consumed);
            const status = entry.permanent ? 308 : 307;
            console.log(`[redirect] HIT pattern ${status} host=${hostname} path=${pathname} -> ${dest}`);
            notify({ entry, kind: "pattern", hostname, pathname, destination: dest, status });
            return server_1.NextResponse.redirect(dest, { status });
        }
        // No redirect matched. Noisy (fires for every non-redirected request), so
        // gated behind REDIRECT_DEBUG=true — set it in Vercel env to trace misses.
        if (process.env.REDIRECT_DEBUG === "true") {
            console.log(`[redirect] MISS host=${hostname} path=${pathname}`);
        }
        return null;
    };
}
const localeUtils_1 = require("./localeUtils");
/**
 * Constants for middleware utilities
 */
exports.COOKIES = {
    vercelJwt: "_vercel_jwt",
    vercelJwtTracking: "_vercel_jwt_tracking",
};
const handleLocaleRedirect = (request) => {
    const url = new URL(request.url);
    const preferredLanguage = request.cookies.get("preferred_language");
    if (!preferredLanguage) {
        return server_1.NextResponse.next();
    }
    const defaultLocale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE || "en";
    const supportedLocales = ["en", "es"];
    const redirectPath = (0, localeUtils_1.getLocaleRedirectPath)({
        pathname: url.pathname,
        preferredLanguage: preferredLanguage?.value,
        supportedLocales,
        defaultLocale,
    });
    if (redirectPath) {
        return server_1.NextResponse.redirect(new URL(redirectPath, request.url));
    }
    return server_1.NextResponse.next();
};
exports.handleLocaleRedirect = handleLocaleRedirect;
const handleErrorRedirect = (request) => {
    const url = new URL(request.url);
    const errorCode = url.searchParams.get("errorCode");
    const errorMessage = url.searchParams.get("errorMessage");
    if (errorCode && /^5[0-9]{2}$/.test(errorCode)) {
        const redirectUrl = new URL("/500", request.url);
        redirectUrl.searchParams.set("errorCode", errorCode);
        if (errorMessage) {
            redirectUrl.searchParams.set("errorMessage", errorMessage);
        }
        return server_1.NextResponse.redirect(redirectUrl);
    }
};
exports.handleErrorRedirect = handleErrorRedirect;
/**
 * Middleware to rewrite the _vercel_jwt cookie with SameSite=None,
 * allowing iframe access from Builder.io editor.
 */
const handleDeploymentProtection = (request, response) => {
    const cookies = request.cookies.getAll();
    const vercelCookie = cookies.find((cookie) => cookie.name === exports.COOKIES.vercelJwt);
    const vercelCookieTracking = cookies.find((cookie) => cookie.name === exports.COOKIES.vercelJwtTracking);
    const isTracked = vercelCookie?.value === vercelCookieTracking?.value;
    if (vercelCookie && !isTracked) {
        const jwt = vercelCookie.value;
        const options = {
            path: '/',
            httpOnly: true,
            secure: true,
            sameSite: 'none',
            maxAge: 90 * 24 * 60 * 60, // 90 days
        };
        response.cookies.set(exports.COOKIES.vercelJwt, jwt, options);
        response.cookies.set(exports.COOKIES.vercelJwtTracking, jwt, options);
    }
    if (!vercelCookie && vercelCookieTracking) {
        response.cookies.delete(exports.COOKIES.vercelJwtTracking);
    }
    return response;
};
exports.handleDeploymentProtection = handleDeploymentProtection;
/**
 * Middleware to redirect URLs with uppercase characters to lowercase.
 * Returns a 301 redirect for SEO-friendly canonical URLs.
 * Returns null if no redirect needed.
 */
const handleCaseInsensitiveUrl = (request) => {
    const url = new URL(request.url);
    const pathname = url.pathname;
    // Skip paths with file extensions (static assets)
    if (/\.[^/]+$/.test(pathname)) {
        return null;
    }
    // Skip section-editors paths — symbol names in Builder.io are case-sensitive
    // and the middleware lowercasing breaks the name lookup
    if (pathname.startsWith("/section-editors/")) {
        return null;
    }
    const lowercasePathname = pathname.toLowerCase();
    if (pathname !== lowercasePathname) {
        const redirectUrl = new URL(request.url);
        redirectUrl.pathname = lowercasePathname;
        // 301 permanent redirect for SEO
        return server_1.NextResponse.redirect(redirectUrl, { status: 301 });
    }
    return null;
};
exports.handleCaseInsensitiveUrl = handleCaseInsensitiveUrl;
